'use client'

import { bandAt, bandLabel, bandValueText, LENGTH_BANDS } from '@repo/core/film-pricing'

import { OrderField } from '@/components/sections/order/order-field.client'
import { type CreatorOfferingView, type CreatorPick, DEFAULT_LENGTH_BAND } from '@/lib/creator'
import { formatPLN } from '@/lib/tiers'
import { META, MICRO } from '@/lib/typography'
import { cn } from '@/lib/utils'

/**
 * "Jak długi ma być film?" — spec §8's length band, as a slider (G5).
 *
 * A SLIDER and not ten checkboxes, and the spec says so in as many words
 * ("suwakiem, nie checkboxami"). The reason it is worth insisting on: the bands
 * are ordered and adjacent, so the question is "how far along", not "which of
 * these ten". A checkbox list makes ten independent decisions out of one.
 *
 * ── ACCESSIBILITY IS THE POINT, NOT A COATING ────────────────────────────
 * This is a native `input[type=range]`, so arrows, Home/End and Page Up/Down
 * all work for free and the browser's own focus ring is the one people expect.
 * What a range input CANNOT do on its own is say what position 3 means — it
 * announces "4 z 10" — so `aria-valuetext` carries the whole sentence in
 * Polish: the range, what is paid now, and that the ceiling is a limit rather
 * than a suggestion. A custom div with `role="slider"` would have meant
 * reimplementing every one of those keys, worse.
 *
 * The price under it is the SAME arithmetic the order total uses (`pickQuantity` →
 * band floor × rate), so the number that moves as the slider moves is the
 * number that ends up on the order. It is set as a STAT rather than as a note
 * beside the label: this control is the one that decides what a film costs, and
 * the figure it produces should be the largest thing in the block.
 */
export function LengthSlider({
  offering,
  pick,
  lineId,
  problem,
  onPatch,
}: {
  offering: CreatorOfferingView
  pick: CreatorPick
  /** Scopes the control id: the same offering can be in the order twice. */
  lineId: string
  problem?: string | null
  onPatch: (next: Partial<CreatorPick>) => void
}) {
  const index = pick.lengthBand ?? DEFAULT_LENGTH_BAND
  const band = bandAt(index) ?? bandAt(DEFAULT_LENGTH_BAND)
  if (!band) return null

  // NET złoty, straight off the chosen level. `null` before a level is picked
  // — the slider is still worth showing then, it just cannot say a price yet.
  const variant = offering.variants.find((v) => v.slug === pick.variantSlug)
  const upfront = variant && !variant.quoteOnly ? variant.priceNet * band.min : null

  return (
    <OrderField
      wide
      htmlFor={`length-${lineId}`}
      label="Jak długi ma być gotowy film?"
      problem={problem}
      hint={`Górna granica to twardy limit — film nie przekroczy ${band.max} minut. Jeśli po pocięciu wyjdzie dłuższy niż opłacone ${band.min}, dopłacasz za nadwyżkę i decydujesz o tym sam, zanim montażysta zrobi resztę.`}
    >
      <output
        htmlFor={`length-${lineId}`}
        className="mb-3 flex flex-wrap items-baseline justify-between gap-x-4 gap-y-1"
      >
        <span className="text-[1.35rem] leading-none font-bold tracking-tight text-white">
          {bandLabel(band)}
        </span>
        {upfront === null ? null : (
          <span className={cn(META, 'text-white/55')}>
            płacisz teraz za {band.min} {band.min === 1 ? 'minutę' : 'minut'} —{' '}
            <span className="font-semibold text-white/85">{formatPLN(upfront)}</span> netto
          </span>
        )}
      </output>

      <input
        id={`length-${lineId}`}
        type="range"
        min={0}
        max={LENGTH_BANDS.length - 1}
        step={1}
        value={index}
        onChange={(event) => onPatch({ lengthBand: Number(event.target.value) })}
        aria-valuetext={bandValueText(band)}
        className="w-full accent-white"
        // The bands are ten discrete rungs, so the browser can draw the
        // detents. Without it the handle glides over positions that do not
        // exist and the control reads as continuous, which it is not.
        list={`length-ticks-${lineId}`}
      />
      <datalist id={`length-ticks-${lineId}`}>
        {LENGTH_BANDS.map((rung) => (
          <option key={rung.index} value={rung.index} />
        ))}
      </datalist>

      {/* The two ends, so the slider's travel is legible without dragging it. */}
      <div className={cn(MICRO, 'mt-1 flex justify-between text-white/55')} aria-hidden>
        <span>do 5 min</span>
        <span>80–90 min</span>
      </div>
    </OrderField>
  )
}
